/**
 * The coach's saved plays over HTTP — `/api/plays`.
 *
 * Listing reads each stored play through `migrate`, so a play saved by an
 * older release opens upgraded. One it cannot read — a newer version, say —
 * is skipped and reported, never deleted: it is still on the server, and a
 * newer app will show it.
 */

import { migrate, type PlayDocument } from '../core/index.ts';
import type { PlayList, PlayStore } from './types.ts';

/** Another of the coach's plays has this name already — saved meanwhile in another tab. */
export class PlayNameTaken extends Error {
  constructor() {
    super('Another of your plays already has this name.');
    this.name = 'PlayNameTaken';
  }
}

export class HttpPlayStore implements PlayStore {
  readonly #getToken: () => Promise<string | null>;
  readonly #fetch: typeof fetch;

  constructor(getToken: () => Promise<string | null>, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
    this.#getToken = getToken;
    this.#fetch = fetchImpl;
  }

  async list(): Promise<PlayList> {
    const response = await this.#request('GET', '/api/plays');
    if (!response.ok) throw new Error(`Could not load your saved plays (${response.status})`);
    return readAllPlays(await response.json());
  }

  async save(play: PlayDocument): Promise<void> {
    const response = await this.#request('PUT', `/api/plays/${encodeURIComponent(play.id)}`, JSON.stringify(play));
    if (response.status === 409) throw new PlayNameTaken();
    if (!response.ok) throw new Error(`Could not save the play (${response.status})`);
  }

  async remove(id: string): Promise<void> {
    const response = await this.#request('DELETE', `/api/plays/${encodeURIComponent(id)}`);
    if (!response.ok && response.status !== 404) throw new Error(`Could not delete the play (${response.status})`);
  }

  async #request(method: 'GET' | 'PUT' | 'DELETE', path: string, body?: string): Promise<Response> {
    const token = await this.#getToken();
    if (!token) throw new Error('Not signed in');
    return this.#fetch(path, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body } : {}),
    });
  }
}

/** Reads what the server returned, keeping every play it can and counting the rest. */
export function readAllPlays(stored: unknown): PlayList {
  const plays: PlayDocument[] = [];
  let unreadable = 0;
  for (const item of Array.isArray(stored) ? stored : []) {
    try {
      plays.push(migrate(item));
    } catch {
      unreadable += 1;
    }
  }
  return { plays, unreadable };
}

/** For tests and for anything that must not hit the network. */
export class MemoryPlayStore implements PlayStore {
  readonly saved = new Map<string, PlayDocument>();

  async list(): Promise<PlayList> {
    return { plays: [...this.saved.values()].sort((a, b) => b.updatedAt - a.updatedAt), unreadable: 0 };
  }

  async save(play: PlayDocument): Promise<void> {
    this.saved.set(play.id, play);
  }

  async remove(id: string): Promise<void> {
    this.saved.delete(id);
  }
}
